import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Send, Mail, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mailtoFor = (q) => {
  const first = (q.contact_name || "there").split(" ")[0];
  const subject = `T.A.C.C. for ${q.company_name} — next step`;
  const body = `Hi ${first},\n\nThanks for reaching out about T.A.C.C. for ${q.company_name} (${q.company_size} people${q.interested_in?.length ? `, interested in ${q.interested_in.join(", ")}` : ""}).\n\nSimplest next step is a 20-minute call — I'll walk you through the eight modules, how teams roll them out shift by shift, and give you a straight number for your seats. Two times that work for you?\n\n— Rich Culmer\nFounder, T.A.C.C. — Through Adversity Comes Change\nhttps://tacc.love/b2b`;
  return `mailto:${q.work_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const HotLeads = ({ onChanged, onOpen }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/admin/leads/hot`).then((r) => setData(r.data)).catch(() => setData({ leads: [], total_open: 0 }));
  useEffect(() => { load(); }, []);

  const reply = async (q) => {
    setBusy(q.id);
    try {
      const r = await axios.post(`${API}/admin/leads/${q.id}/reply`);
      toast[r.data.email_ok ? "success" : "message"](
        r.data.email_ok ? `Intro sent to ${q.contact_name} at ${q.company_name}.` : `Marked contacted — the email to ${q.work_email} didn't send (see Emails tab).`
      );
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the reply.");
    } finally {
      setBusy(null);
    }
  };

  if (!data || !data.leads.length) return null;

  return (
    <section className="mb-8" data-testid="hot-leads">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Flame size={14} className="text-[#B45309]" />
        <div className="section-tag">§ Hot leads — reply to these first</div>
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#555]">ranked by size · tier · interests · freshness · {data.total_open} open</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-3">
        {data.leads.map((q, i) => (
          <article key={q.id} className={`tacc-card p-5 flex flex-col gap-3 ${i === 0 ? "border-[#F59E0B]/60" : ""}`} data-testid={`hot-lead-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#B45309]">#{i + 1} · score {q.score}</div>
                <button onClick={() => onOpen?.(q)} className="font-display text-xl leading-tight truncate mt-1 text-left hover:text-[#B45309] transition-colors block max-w-full" title="Open this quote" data-testid={`hot-lead-open-${i}`}>{q.company_name}</button>
                <div className="text-xs text-[#78716C] truncate">{q.contact_name} · {q.work_email}</div>
              </div>
              {q.status === "contacted" && <CheckCircle2 size={16} className="text-emerald-400 shrink-0" title="Contacted" />}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(q.reasons || []).map((r) => (
                <span key={r} className="font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1 border border-[#E7DFD5] text-[#44403C]">{r}</span>
              ))}
            </div>
            <p className="text-xs text-[#78716C] leading-relaxed line-clamp-3" title={q.message}>{q.message}</p>
            <div className="flex flex-wrap gap-2 mt-auto pt-2">
              <button
                onClick={() => reply(q)}
                disabled={busy === q.id || q.status === "contacted"}
                className="btn-ember text-xs !px-3 !py-2 disabled:opacity-50"
                data-testid={`hot-lead-reply-${i}`}
              >
                <Send size={12} /> {q.status === "contacted" ? "Replied" : "Reply now"}
              </button>
              <a href={mailtoFor(q)} className="btn-outline text-xs !px-3 !py-2" data-testid={`hot-lead-mailto-${i}`}>
                <Mail size={12} /> Open in my email
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
